/**
 * Keeps the "Order on WhatsApp" deep link in sync with the selected variant.
 *
 * The message itself is built in Liquid (blocks/whatsapp-order.liquid). When a
 * shopper picks a different size, Horizon has already re-rendered the product
 * page into a detached document to update the price and inventory blocks — this
 * just lifts the freshly rendered href out of that document. No message
 * formatting is duplicated here, so the Liquid stays the single source of truth.
 */
class WhatsAppOrder extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    document.addEventListener('shopify:product:select', this.#handleProductSelect, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /**
   * @param {Event & { promise?: Promise<{ detail?: { html?: Document, productId?: string } }> }} event
   */
  #handleProductSelect = (event) => {
    // Variant pickers inside product cards and quick-add drawers fire the same
    // event; they aren't this product.
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        const doc = detail?.html;
        if (!doc) return;

        const blockId = this.dataset.blockId;
        if (!blockId) return;

        const next = doc.querySelector(
          `whatsapp-order[data-block-id="${CSS.escape(blockId)}"] a[href]`
        );
        const current = this.querySelector('a[href]');
        const href = next?.getAttribute('href');

        if (href && current) current.setAttribute('href', href);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[whatsapp-order] variant sync failed:', error);
      });
  };
}

if (!customElements.get('whatsapp-order')) {
  customElements.define('whatsapp-order', WhatsAppOrder);
}
